import { FC, memo, useContext, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import {
    MAX_INSTALLMENTS,
    MIN_CREDIT_INSTALLMENTS,
    calculateMaxPayments,
    isSplitAllowed,
    linkedSplitOptionsForAmount
} from '../../../../api/pelecardUtils/pelecardInstallments';

const CREDIT_INSTALLMENT_COUNTS: number[] = [];
for (let n = MIN_CREDIT_INSTALLMENTS; n <= MAX_INSTALLMENTS; n += 1) CREDIT_INSTALLMENT_COUNTS.push(n);

const planMessageSx = { mt: 1.5, fontSize: 13, color: 'text.secondary' };

type Props = {
    /** Formik path to the paymentMethodObject fields this writes to. Defaults to
     *  'paymentMethodObject' (Receipt/PreReceipt/Remittance's shared shape). */
    fieldPrefix?: string;
    tripDate: string | null;
    fullPaymentWindowDays: number;
    disabled?: boolean;
    /** Offer the dollar-linked split for a USD receipt. Only a receipt on a
     *  travel file: the server refuses a split with no travel file to charge. */
    allowDollarLinked?: boolean;
};

/**
 * New-card (Masleka) instalment / credit-deal picker — mirrors tourRegistrationApp's
 * Step2ReviewPay rules exactly (see docs/tour-registration-payment-rules.md):
 *   - USD: a DOLLAR-LINKED split (תשלומים צמודי דולר), on a terminal approved for
 *     it — never a credit/interest deal. The page only checks the card in $; the
 *     server charges it afterwards in ₪, linked to the dollar (linkedSplit.ts).
 *   - ILS: a CREDIT deal (עסקת קרדיט, with interest) only — never a plain split.
 * A credit deal settles in ILS (CreditCardSubForm converts a USD amount at the
 * document's own rate before sending it) — Pelecard's manual: "תשלומים ניתן
 * לבצע בש"ח בלבד".
 */
const CreditCardPaymentPlanFields: FC<Props> = ({
    fieldPrefix = 'paymentMethodObject',
    tripDate,
    fullPaymentWindowDays,
    disabled,
    allowDollarLinked = false
}) => {
    const { values, setFieldValue } = useFormikContext<any>();
    const currencies = useContext(CurrenciesContext);

    const currencyCode = useMemo(() => {
        const raw = values.currency;
        if (typeof raw === 'object') {
            return raw?.code || currencies.getCodeByCurrency(raw) || '';
        }
        // Formik sometimes stores the currency as its database id and
        // sometimes as the already-normalized ISO code. The latter must not
        // be looked up as an id, otherwise neither the instalment nor credit
        // branch renders and the payment-plan control appears to be missing.
        return currencies.getCodeByCurrency(raw) || String(raw || '');
    }, [values.currency, currencies]);

    const isIls = currencyCode.toUpperCase() === 'ILS';
    const isUsd = currencyCode.toUpperCase() === 'USD';

    const field = (name: string) => `${fieldPrefix}.${name}`;
    const numberOfPayments = Number(values?.[fieldPrefix]?.numberOfPayments) || 1;
    const isCreditDeal = !!values?.[fieldPrefix]?.isCreditDeal;
    const creditInstallments = Number(values?.[fieldPrefix]?.creditInstallments) || MIN_CREDIT_INSTALLMENTS;

    // No trip date at all — a supplier/agent payment or a non-tour service
    // travel file — has no departure to gate against, so the "N days before
    // the trip" rule is skipped rather than failing closed on a date that
    // was never going to exist. A passenger's OWN trip missing its date
    // still blocks (that's a data problem, not "no trip"). Must agree with
    // CreditCardSubForm.tsx's `tripDateApplies: !!tripDate` sent to
    // /init-paymentGW, or this offers a count the server then refuses.
    const tripDateApplies = !!tripDate;
    const splitAllowed = useMemo(
        () => !tripDateApplies || isSplitAllowed(tripDate, fullPaymentWindowDays),
        [tripDateApplies, tripDate, fullPaymentWindowDays]
    );

    const splitUnavailableMessage = tripDateApplies && !splitAllowed
        ? `Payment splitting is unavailable within ${fullPaymentWindowDays} days of the trip.`
        : '';

    // The dollar-linked split, judged by the same rules the server re-applies in
    // resolveLinkedSplitPlan: a USD receipt on a travel file, a terminal approved
    // for dollar linking, outside the full-payment window, and a count that
    // `linkedSplitOptionsForAmount` offers for the shekel amount. The server
    // converts at the latest representative rate; the document's own rate is
    // the closest estimate here, so a count right at the floor may still be
    // refused there — never charged as something else.
    const terminal = values?.[fieldPrefix]?.paymentsTerminal;
    const terminalLinksDollar = !!terminal?.allowsDollarLinked && !!terminal?.allowsForeignCurrency;
    const linkedSplitCounts = useMemo(() => {
        if (!allowDollarLinked || !isUsd || !terminalLinksDollar || !splitAllowed) return [1];
        const amountIls = (Number(values?.amount) || 0) * (Number(values?.rate) || 0);
        return linkedSplitOptionsForAmount(amountIls, {
            maxPayments: tripDateApplies ? calculateMaxPayments(tripDate) : MAX_INSTALLMENTS,
            maxInstallments: terminal?.maxInstallments,
            minPaymentAmount: terminal?.minPaymentAmount
        });
    }, [
        allowDollarLinked,
        isUsd,
        terminalLinksDollar,
        splitAllowed,
        values?.amount,
        values?.rate,
        tripDateApplies,
        tripDate,
        terminal?.maxInstallments,
        terminal?.minPaymentAmount
    ]);

    // A count is only ever sent under the split that offered it: anything the
    // current amount, terminal or currency no longer offers falls back to a
    // single payment, and a credit deal is cleared whenever the document is not
    // ILS, so a stale choice can never be sent for a currency it was never
    // offered under.
    useEffect(() => {
        if (numberOfPayments > 1 && !linkedSplitCounts.includes(numberOfPayments)) {
            setFieldValue(field('numberOfPayments'), 1);
        }
        if (!isIls && isCreditDeal) setFieldValue(field('isCreditDeal'), false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIls, numberOfPayments, linkedSplitCounts]);

    if (isIls) {
        if (!splitAllowed) return <Typography sx={planMessageSx}>{splitUnavailableMessage}</Typography>;
        return (
            <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isCreditDeal}
                            disabled={disabled}
                            onChange={(event) => setFieldValue(field('isCreditDeal'), event.target.checked)}
                        />
                    }
                    label="I'm interested in a credit deal (with interest)"
                />
                {isCreditDeal && (
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Number of instalments"
                        value={creditInstallments}
                        onChange={(event) =>
                            setFieldValue(field('creditInstallments'), Number(event.target.value) || MIN_CREDIT_INSTALLMENTS)
                        }
                        disabled={disabled}
                    >
                        {CREDIT_INSTALLMENT_COUNTS.map((count) => (
                            <MenuItem key={count} value={count}>
                                {count}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>
        );
    }

    if (isUsd && allowDollarLinked) {
        // Shown disabled, with the reason, whenever the split is not on offer.
        const linkedUnavailableMessage = !terminalLinksDollar
            ? 'This terminal is not approved for installments linked to the dollar.'
            : splitUnavailableMessage ||
              (linkedSplitCounts.length < 2 ? 'This amount is too small to split into payments.' : '');
        return (
            <Box sx={{ mt: 1.5 }}>
                <TextField
                    select
                    size="small"
                    fullWidth
                    label="Number of payments (linked to the dollar)"
                    value={linkedSplitCounts.includes(numberOfPayments) ? numberOfPayments : 1}
                    onChange={(event) => setFieldValue(field('numberOfPayments'), Number(event.target.value) || 1)}
                    disabled={disabled || linkedSplitCounts.length < 2}
                >
                    {linkedSplitCounts.map((count) => (
                        <MenuItem key={count} value={count}>
                            {count}
                        </MenuItem>
                    ))}
                </TextField>
                <Typography sx={planMessageSx}>
                    {linkedUnavailableMessage ||
                        'Charged in shekels at the representative rate, each payment linked to the dollar.'}
                </Typography>
            </Box>
        );
    }

    // Not ILS, and no dollar-linked split here: Pelecard clears no other
    // multi-payment deal in any currency but shekels (065), so a single payment
    // is all that is on offer.
    return (
        <Typography sx={planMessageSx}>
            A payment plan (עסקת קרדיט) is only available when charging in ILS.
        </Typography>
    );
};

export default memo(CreditCardPaymentPlanFields);
